import { Injectable } from '@angular/core'
import { CanLoad, Route, Router, UrlSegment } from '@angular/router'
import { AuthService } from '@app/services/auth.service'
import { Observable, of } from 'rxjs'
import { ApiService } from 'src/app/services/api.service'
import { filter, switchMap, take, catchError } from 'rxjs/operators'
import { SettingsService } from '@app/services/settings.service'
import { TOKEN_KEY } from "@environments/environment";

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanLoad {
  constructor(
    private apiService: ApiService,
    private router: Router,
    private authService: AuthService,
    private settingsService: SettingsService,
  ) {
  }

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
    const cookies = this.apiService.extractCookies()
    const token = cookies['backend_auth_token']

    if (token) {
      const payload = this.apiService.extractPayload(token);
      const { user, client, valid_until, scope } = payload

      if (scope.find((e) => e === 'backend') && parseInt(valid_until) > Math.round(new Date().getTime() / 1000)) {
        this.apiService.deleteCookie('backend_auth_token')

        localStorage.setItem('user', JSON.stringify(user))
        localStorage.setItem(TOKEN_KEY, token)

        this.authService.userValue = user
        this.authService.loadToken().then()

        return new Observable((subscriber) => {
          subscriber.next(true);
        });
      } else {
        localStorage.removeItem('user')
        localStorage.removeItem(TOKEN_KEY)
        this.authService.loadToken().then()
      }
    }

    return this.authService.isAuthenticated.pipe(filter((isAuthenticated) => isAuthenticated !== null),
      take(1),
      switchMap((isAuthenticated) => {
        if (isAuthenticated) {
          return this.authService.user
        } else {
          return this.authService.getValidSession().then((res) => {
            if (res?.user === null || res?.client === null) {
              if(res?.forceSingleSignOn){
                this.authService.forceSingleSignOn.next(res?.forceSingleSignOn)
                return null
              }
              if (res?.redirectUrl) {
                window.location.href = res.redirectUrl
                return null
              }
            }
            return res?.user ? res?.user : null
          })
        }
      }),
      switchMap((user) => {
        if (user) {
          return Promise.all([
            this.settingsService.getAvailableModules(),
            this.settingsService.getUserRoles()
          ]).then(([availableModules, userRoles]) => {
            // Check if user has admin role for admin-only routes
            const isAdmin = userRoles.includes('admin');
            const adminOnlyRoutes = ['servers'];
            
            return segments.some((currentSegment) => {
              // Always allow dashboard
              if (currentSegment.path === 'dashboard') {
                return true;
              }
              
              // Allow available modules
              if (availableModules.includes(currentSegment.path)) {
                return true;
              }
              
              // Allow admin routes only for admin users
              if (adminOnlyRoutes.includes(currentSegment.path) && isAdmin) {
                return true;
              }
              
              return false;
            });
          })
        } else {
          this.router.navigateByUrl('/login')
          return of(false)
        }
      }),
      catchError((error) => {
        this.router.navigateByUrl('/login')
        return of(false)
      }),
      // Add a final switchMap to handle the case when none of the conditions match
      switchMap((isAuthorized) => {
        if (!isAuthorized) {
          this.authService.clearUserAndCredentials().then(() => {
            this.router.navigateByUrl('/login')
          });
          return of(false)
        }
        return of(isAuthorized)
      }),
    )
  }
}
